import { useState, useEffect, useRef, useContext } from 'react';
import { flushSync } from 'react-dom';
import { Modal } from 'bootstrap';
import ExitIntent from '../utils/exitIntent';
import { exitModalData } from '../utils/exitModalData';
import { CartContext } from '../store/CartContext';
import useBrand from '../hooks/useBrand';
import API from '../services/apiService';

import '../scss/exit-modal.scss';

// Testable Exit Modal Component, these variables will need to be set by the testing platform (like Convert or others)
// window.testShowExitModal = true/false - force show modal for testing
// window.testUseNewUI = true/false - force use new UI for testing
// window.testExitModalCoupon = 'COUPONCODE' - force use a specific coupon code for testing

export default function ExitModal() {
  const { domain } = useBrand();
  const [cartState, cartDispatch] = useContext(CartContext);

  const modalData = exitModalData[domain] || null;
  const isCartEmpty = !cartState?.cartProducts?.length;
  const isSingleProduct = cartState?.cartProducts?.length === 1;
  const currentProduct = cartState?.cartProducts ? cartState?.cartProducts[0] : null;
  const productId = isSingleProduct && currentProduct ? currentProduct?.productId : null;

  // check if domain's productIds is an empty array, meaning all products are eligible
  const isDomainEligible = modalData?.productIds?.length === 0;

  // check if product in the cart can use the coupon based the listed productIds
  const isProductEligible = modalData?.productIds?.some((id) => id === productId);

  // check if a coupon is already applied
  const isCouponApplied = cartState?.cartProducts?.some((cartProduct) => Boolean(cartProduct.discountSource));

  // summing up all the check to determine if we can show the exit modal
  const canUseExitModal = Boolean(
    !isCartEmpty && !isCouponApplied && (isDomainEligible || (isSingleProduct && isProductEligible))
  );

  const isTesting = modalData?.isTesting || false;

  const exitModal = useRef();

  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const [useNewUI, setUseNewUI] = useState(modalData?.useNewUI || false);

  const [modal, setModal] = useState(null);

  // const cartProductsIds = cartState?.cartProducts
  //   ?.map((item) => {
  //     const productId = item.productId;
  //     if (item.upSellProducts.length > 0) {
  //       const selectedUpsells = item.upSellProducts
  //         .filter((upsell) => upsell.isSelected)
  //         .map((selected) => selected.productId);
  //       return [productId, ...selectedUpsells];
  //     } else {
  //       return productId;
  //     }
  //   })
  //   .flat();

  // const isCouponApplied = cartState?.cartProducts?.some((product) => {
  //   const appliedToMainProduct = product.discountSource !== null;
  //   if (appliedToMainProduct) return appliedToMainProduct;
  //   const isAppliedToUpsell = product.upSellProducts.some((upsell) => upsell.discountSource !== null);
  //   return isAppliedToUpsell;
  // });

  useEffect(() => {
    // initialize the modal only if the cart can use an exit modal
    if (canUseExitModal) {
      const modal = new Modal(exitModal.current, {
        backdrop: 'static',
      });

      setModal(modal);

      const exitIntent = ExitIntent({
        threshold: 10,
        eventThrottle: 200,
        delay: 2000,
        onExitIntent: () => {
          // flushSync renders the new UI right away so the modal doesn't show the old layout first
          // this is read here (not at init) because the testing platform may set it after the component mounts
          if (window.testUseNewUI !== undefined) {
            flushSync(() => setUseNewUI(window.testUseNewUI));
          }

          // when testing, only the variant shows the modal
          // window.testShowExitModal must be set to true by the testing platform
          if (isTesting && !window.testShowExitModal) return;

          // Push custom event to dataLayer
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'view_abandon_modal' });
          modal.show();
        },
      });

      return () => {
        exitIntent();
        // dispose removes the bootstrap listeners and any leftover backdrop
        modal.dispose();
        setModal(null);
      };
    }
  }, [canUseExitModal, isTesting]);

  const applyCoupon = (event) => {
    event.preventDefault();
    if (isApplyingCoupon) return;

    // Push custom event to dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'add_coupon_abandon_modal' });

    setIsApplyingCoupon(true);

    const cartData = {
      cartId: cartState.cartId,
      productIds: [],
      pricingVariant: null,
      groupId: null,
      coupon: window.testExitModalCoupon || modalData.coupon,
      sourceHost: cartState.sourceHost,
    };
    API.updateCart(cartData)
      .then((res) => {
        const updatedCart = res.data;

        let isCartUpdated = false;
        let fallbackTimer;

        const updateCartState = () => {
          if (isCartUpdated) return;
          isCartUpdated = true;
          clearTimeout(fallbackTimer);

          cartDispatch({
            type: 'UPDATE',
            payload: {
              ...updatedCart,
              isLoading: false,
            },
          });
        };

        // the cart update makes canUseExitModal false and unmounts this component,
        // so let Bootstrap finish the hide animation first to avoid a stuck backdrop
        // isApplyingCoupon stays true until then so the button can't be clicked again
        exitModal.current?.addEventListener('hidden.bs.modal', updateCartState, { once: true });

        // fallback in case hidden.bs.modal never fires (e.g. hide() is a no-op)
        fallbackTimer = setTimeout(updateCartState, 1000);

        modal.hide();
      })
      .catch((err) => {
        setIsApplyingCoupon(false);

        console.error(err?.response?.data);

        // show error message or reload the page
        cartDispatch({
          type: 'ERROR',
          payload: {
            isLoading: false,
            isError: true,
            error: err?.response?.data || 'General Error',
          },
        });

        if (err?.response?.data?.status >= 400) {
          console.error('Coupon was not applied!');
        }
      });
  };

  // dont render anything if the cart cant use the exit modal
  if (!canUseExitModal) {
    return null;
  }

  return (
    <>
      <div className="modal fade text-center" ref={exitModal} id="exit-modal" tabIndex="-1" aria-hidden="true">
        <div className="exit-modal modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-3 p-md-4">
              {/* <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button> */}
              <div className="row g-0 gx-4">
                {useNewUI && (
                  <div className="col-lg-4 d-none d-lg-flex">
                    <img
                      className="coupon-img"
                      alt="Coupon image"
                      src="data:image/svg+xml;base64,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"
                    />
                  </div>
                )}

                <div className={`col-12 ${useNewUI ? 'col-lg-8' : ''} py-3`}>
                  <h3 className="discount-headline fw-700 h5 mt-0 mb-3">{modalData.headline}</h3>
                  <p className="discount-amount fw-700 lh-12 my-4">{modalData.discount}</p>
                  <p className="discount-subcopy fw-500">{modalData.subCopy}</p>
                  <p className="mb-0">
                    <button className="exit-btn" onClick={applyCoupon} disabled={isApplyingCoupon}>
                      {isApplyingCoupon && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      )}
                      Continue
                    </button>
                  </p>
                  {/* <p className="small mt-3 mb-0">
                    <a className="text-muted" href="#" data-bs-dismiss="modal" aria-label="Close">
                      No thanks, I{"'"}d rather pay full price.
                    </a>
                  </p> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
